import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Spinner } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import {
  FaCheck,
  FaChevronRight,
  FaChartLine,
  FaUsers,
  FaBook,
  FaGraduationCap,
  FaLaptop,
  FaBrain,
  FaRocket,
  FaShieldAlt,
  FaArrowRight,
  FaClipboardCheck,
  FaVideo,
  FaCertificate,
  FaTrophy,
  FaBuilding,
  FaHeadset,
  FaUserGraduate,
  FaChalkboardTeacher,
} from 'react-icons/fa';
import axios from 'axios';
import { getApiBaseUrl } from '../../utils/apiBase';
import LandingNavbar from './Navbar';
import LandingLogo from './LandingLogo';
import ScrollReveal from './ScrollReveal';
import FeaturedCourses from '../../components/Landing/FeaturedCourses';
import EduGenieShowcase from '../../components/Landing/EduGenieShowcase';
import { LIBERATE_CONTACT, LIBERATE_AWARDS, LIBERATE_TESTIMONIALS } from './landingConstants';
import {
  HERO_FALLBACK,
  TRUST_STATS,
  PLATFORM_STATS,
  AUDIENCES,
  PRODUCT_PILLARS,
  SOLUTIONS,
  HOW_IT_WORKS,
  HERO_HIGHLIGHTS,
  ENTERPRISE_POINTS,
} from './landingContent';
import './landing-animations.css';
import './landing-theme.css';

const PILLAR_ICONS = {
  courses: FaBook,
  assessments: FaClipboardCheck,
  vilt: FaVideo,
  certificates: FaCertificate,
  gamification: FaTrophy,
  enterprise: FaBuilding,
  analytics: FaChartLine,
  edugenie: FaBrain,
};

const FEATURE_ICONS = [FaUsers, FaBook, FaChartLine, FaBrain, FaLaptop, FaGraduationCap, FaShieldAlt, FaRocket];

const AUDIENCE_ICONS = [FaBuilding, FaChalkboardTeacher, FaUserGraduate];

const SOLUTION_ICONS = [FaRocket, FaGraduationCap, FaShieldAlt, FaUsers, FaLaptop, FaHeadset];

const isSectionEnabled = (value) => value === true || value === 1 || value === '1';

const renderHeroTitle = (title) => {
  if (!title) return HERO_FALLBACK.title;
  if (title.includes('Liberate')) {
    const [before, ...rest] = title.split('Liberate');
    return (
      <>
        {before}
        <em>Liberate{rest.join('Liberate')}</em>
      </>
    );
  }
  if (title.includes('Liberating')) {
    const [before, ...rest] = title.split('Liberating');
    return (
      <>
        {before}
        <em>Liberating{rest.join('Liberating')}</em>
      </>
    );
  }
  return title;
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [landingPageData, setLandingPageData] = useState(null);
  const [features, setFeatures] = useState([]);
  const [config, setConfig] = useState({
    hero_title: HERO_FALLBACK.title,
    hero_subtitle: HERO_FALLBACK.subtitle,
    hero_cta_text: HERO_FALLBACK.ctaText,
    hero_cta_url: HERO_FALLBACK.ctaUrl,
    extra_content_html: '',
    is_active: true,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const baseUrl = getApiBaseUrl();
        const configResponse = await axios.get(`${baseUrl}/public/landing-page-config`);

        if (configResponse.data?.success && configResponse.data.data) {
          const dbConfig = configResponse.data.data;
          setLandingPageData(dbConfig);
          setConfig({
            hero_title: dbConfig.hero_title || HERO_FALLBACK.title,
            hero_subtitle: dbConfig.hero_subtitle || HERO_FALLBACK.subtitle,
            hero_cta_text: dbConfig.hero_cta_text || HERO_FALLBACK.ctaText,
            hero_cta_url: dbConfig.hero_cta_url || HERO_FALLBACK.ctaUrl,
            extra_content_html: dbConfig.extra_content_html || '',
            is_active: dbConfig.is_active !== undefined ? dbConfig.is_active : true,
          });

          if (dbConfig.show_features) {
            try {
              const featuresRes = await axios.get(`${baseUrl}/features/public`);
              if (featuresRes.data?.success) {
                setFeatures(featuresRes.data.data || []);
              }
            } catch (featuresError) {
              console.error('Error fetching features:', featuresError);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching landing page config:', error);
        const fallbackValues = {
          show_banners: 1,
          show_features: 1,
          show_awards: 1,
          show_testimonials: 1,
          show_faqs: 0,
          hero_title: HERO_FALLBACK.title,
          hero_subtitle: HERO_FALLBACK.subtitle,
          hero_cta_text: HERO_FALLBACK.ctaText,
          hero_cta_url: HERO_FALLBACK.ctaUrl,
          extra_content_html: '',
          is_active: true,
        };
        setLandingPageData(fallbackValues);
        setConfig({
          hero_title: fallbackValues.hero_title,
          hero_subtitle: fallbackValues.hero_subtitle,
          hero_cta_text: fallbackValues.hero_cta_text,
          hero_cta_url: fallbackValues.hero_cta_url,
          extra_content_html: fallbackValues.extra_content_html,
          is_active: fallbackValues.is_active,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const roleStr = String(user.role).toLowerCase();
    const roleNum = parseInt(user.role, 10);

    if (roleStr === 'admin' || roleNum === 0) {
      navigate('/dashboard/admin', { replace: true });
    } else if (roleStr === 'user' || roleNum === 1) {
      navigate('/dashboard/user', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    if (loading) return;
    const { hash } = window.location;
    if (!hash) return;
    const timer = window.setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [loading]);

  const handleCtaClick = () => {
    const targetUrl = config.hero_cta_url || '/login';
    if (targetUrl.startsWith('http')) {
      window.open(targetUrl, '_blank');
      return;
    }
    navigate(targetUrl);
  };

  const showCmsFeatures = features.length > 0;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!config.is_active) {
    return (
      <div className="landing-page d-flex flex-column justify-content-center align-items-center min-vh-100 text-center px-3">
        <LandingLogo variant="light" layout="lockup" showParentLink size="lg" to={null} />
        <h1 className="mt-4 fw-bold">Welcome to Enable LXP</h1>
        <p className="mb-4 text-muted">Our site is currently under maintenance. Please check back soon.</p>
        <button type="button" className="landing-btn-primary" onClick={() => navigate('/login')}>
          Sign In
        </button>
      </div>
    );
  }

  return (
    <div className="landing-page min-vh-100 d-flex flex-column">
      <ScrollReveal />
      <LandingNavbar />

      {/* Hero */}
      <section className="landing-hero pt-5" style={{ paddingTop: '96px' }}>
        <div className="landing-hero-accent" aria-hidden="true" />
        <div className="landing-hero-accent-left" aria-hidden="true" />

        <Container className="position-relative py-5">
          <Row className="align-items-center py-lg-4">
            <Col lg={6} className="mb-5 mb-lg-0">
              <div className="text-center text-lg-start">
                <span className="landing-hero-line d-none d-lg-block animate-fade-up" />
                <p className="landing-hero-eyebrow animate-fade-up mb-0">
                  Enable LXP · Powered by Liberate Global
                </p>

                <h1 className="landing-hero-title animate-fade-up animation-delay-100">
                  {renderHeroTitle(config.hero_title)}
                </h1>

                {config.hero_subtitle && (
                  <p className="landing-hero-subtitle mb-4 mx-auto mx-lg-0 animate-fade-up animation-delay-200">
                    {config.hero_subtitle}
                  </p>
                )}

                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-start animate-fade-up animation-delay-300">
                  <button type="button" className="landing-btn-primary" onClick={() => navigate('/enquiry')}>
                    Book a Demo
                    <FaArrowRight size={14} aria-hidden="true" />
                  </button>
                  <button type="button" className="landing-btn-secondary" onClick={handleCtaClick}>
                    {config.hero_cta_text || 'Sign In'}
                  </button>
                  <button
                    type="button"
                    className="landing-btn-outline"
                    onClick={() => navigate('/courses')}
                    aria-label="Explore courses"
                  >
                    Explore Courses
                  </button>
                </div>

                <div className="landing-hero-proof animate-fade-up animation-delay-300 justify-content-center justify-content-lg-start">
                  <span>SCORM-ready</span>
                  <span className="landing-hero-proof-dot" aria-hidden="true" />
                  <span>EduGenie AI</span>
                  <span className="landing-hero-proof-dot" aria-hidden="true" />
                  <span>Enterprise multi-org</span>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              <div className="landing-hero-visual animate-fade-right animation-delay-200">
                <div className="landing-mock-dashboard">
                  <div className="landing-mock-bar">
                    <span className="landing-mock-dot" style={{ background: '#B32025' }} />
                    <span className="landing-mock-dot" style={{ background: '#d4d4d4' }} />
                    <span className="landing-mock-dot" style={{ background: '#d4d4d4' }} />
                  </div>
                  <div className="p-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <LandingLogo variant="light" layout="lockup" size="sm" to={null} />
                      <span className="badge rounded-pill" style={{ background: '#B32025' }}>
                        Live
                      </span>
                    </div>
                    <h3 className="fw-bold text-dark mb-1 fs-5">Learning Command Center</h3>
                    <p className="text-muted small mb-3">
                      Courses, VILT, assessments, certificates — and EduGenie in one place.
                    </p>

                    {[
                      { label: 'Leadership Essentials', pct: 78, icon: FaGraduationCap },
                      { label: 'Compliance 2026', pct: 92, icon: FaShieldAlt },
                      { label: 'Sales Enablement Path', pct: 54, icon: FaRocket },
                    ].map(({ label, pct, icon: Icon }) => (
                      <div key={label} className="landing-mock-course-row">
                        <span className="landing-mock-course-icon" aria-hidden="true">
                          <Icon />
                        </span>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between small fw-semibold">
                            <span>{label}</span>
                            <span style={{ color: '#B32025' }}>{pct}%</span>
                          </div>
                          <div className="landing-mock-progress" aria-hidden="true">
                            <span style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className="mt-3 pt-2">
                      {HERO_HIGHLIGHTS.map((item) => (
                        <div key={item} className="d-flex align-items-center gap-2 mb-2">
                          <FaCheck style={{ color: '#B32025', flexShrink: 0 }} aria-hidden="true" />
                          <span className="small text-secondary">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Trust strip */}
      <div className="landing-trust-strip">
        <Container>
          <Row className="g-4 text-center">
            {TRUST_STATS.map(({ value, label }) => (
              <Col md={3} sm={6} key={label}>
                <div className="landing-stat-block">
                  <div className="landing-stat-value">{value}</div>
                  <p className="landing-stat-label mb-0">{label}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </div>

      {/* Audiences — like LearnWorlds “Any business” */}
      <section id="audiences" className="py-5 bg-white">
        <Container>
          <div className="text-center mb-5 reveal reveal-up">
            <p className="landing-section-eyebrow">Built for every stakeholder</p>
            <h2 className="landing-section-title mb-3">One platform. Every learning role.</h2>
            <p className="landing-section-desc mx-auto mb-0">
              From enterprise admins to frontline learners — Enable LXP delivers the right experience for each audience.
            </p>
          </div>
          <Row className="g-4">
            {AUDIENCES.map(({ title, desc, points }, index) => {
              const Icon = AUDIENCE_ICONS[index % AUDIENCE_ICONS.length];
              return (
                <Col md={4} key={title}>
                  <article className="landing-audience-card reveal reveal-up">
                    <div className="landing-feature-icon mb-3" style={{ width: 48, height: 48, fontSize: '1.2rem' }}>
                      <Icon aria-hidden="true" />
                    </div>
                    <h3 className="fw-bold fs-5 mb-2">{title}</h3>
                    <p className="text-muted small mb-0">{desc}</p>
                    <ul>
                      {points.map((point) => (
                        <li key={point}>
                          <FaCheck style={{ color: '#B32025', marginTop: 3, flexShrink: 0 }} aria-hidden="true" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </article>
                </Col>
              );
            })}
          </Row>
        </Container>
      </section>

      {/* Product pillars — KREDO “Everything you need” */}
      <section id="features" className="py-5 landing-about-band">
        <Container>
          <div className="text-center mb-5 reveal reveal-up">
            <p className="landing-section-eyebrow">Everything you need to engage &amp; evolve</p>
            <h2 className="landing-section-title mb-3">A complete learning stack — not just courses</h2>
            <p className="landing-section-desc mx-auto mb-0">
              Enable LXP combines content delivery, live training, assessment, recognition, analytics, and AI support in one Liberate platform.
            </p>
          </div>
          <Row className="g-4">
            {PRODUCT_PILLARS.map((pillar, index) => {
              const Icon = PILLAR_ICONS[pillar.key] || FEATURE_ICONS[index % FEATURE_ICONS.length];
              return (
                <Col md={6} lg={3} key={pillar.key}>
                  <article className="landing-pillar-card reveal reveal-up">
                    <div className="landing-pillar-num">0{index + 1}</div>
                    <div className="landing-feature-icon mb-3" style={{ width: 44, height: 44, fontSize: '1.1rem' }}>
                      <Icon aria-hidden="true" />
                    </div>
                    <h3 className="fw-bold fs-6 mb-2">{pillar.title}</h3>
                    <p className="text-muted small mb-0">{pillar.description}</p>
                  </article>
                </Col>
              );
            })}
          </Row>

          {landingPageData && isSectionEnabled(landingPageData.show_features) && showCmsFeatures && (
            <Row className="g-4 mt-2">
              {features.map((feature, index) => {
                const Icon = FEATURE_ICONS[index % FEATURE_ICONS.length];
                return (
                  <Col md={6} lg={4} key={feature.id || index}>
                    <div className="landing-feature-card reveal reveal-up">
                      <div className="landing-feature-icon">
                        <Icon aria-hidden="true" />
                      </div>
                      <h4 className="fw-bold mb-3">{feature.title || `Feature ${index + 1}`}</h4>
                      <p className="text-muted mb-0">{feature.description || 'Feature description not available.'}</p>
                      {feature.link && (
                        <a href={feature.link} className="landing-link-arrow mt-3">
                          Learn More <FaChevronRight size={12} aria-hidden="true" />
                        </a>
                      )}
                    </div>
                  </Col>
                );
              })}
            </Row>
          )}
        </Container>
      </section>

      {/* EduGenie */}
      <EduGenieShowcase />

      {/* Solutions — KREDO tailored solutions */}
      <section id="solutions" className="py-5 bg-white">
        <Container>
          <div className="text-center mb-5 reveal reveal-up">
            <p className="landing-section-eyebrow">Tailored for every business need</p>
            <h2 className="landing-section-title mb-3">Solutions that drive workforce outcomes</h2>
            <p className="landing-section-desc mx-auto mb-0">
              Whether you onboard, upskill, or prove compliance — Enable LXP is built for measurable readiness.
            </p>
          </div>
          <Row className="g-4">
            {SOLUTIONS.map(({ title, desc }, index) => {
              const Icon = SOLUTION_ICONS[index % SOLUTION_ICONS.length];
              return (
                <Col md={6} lg={4} key={title}>
                  <article className="landing-solution-card reveal reveal-up">
                    <div className="landing-feature-icon mb-3" style={{ width: 44, height: 44, fontSize: '1.1rem' }}>
                      <Icon aria-hidden="true" />
                    </div>
                    <h3 className="fw-bold fs-5 mb-2">{title}</h3>
                    <p className="text-muted small mb-0">{desc}</p>
                  </article>
                </Col>
              );
            })}
          </Row>
        </Container>
      </section>

      {/* How it works */}
      <section className="py-5 landing-about-band">
        <Container>
          <div className="text-center mb-5 reveal reveal-up">
            <p className="landing-section-eyebrow">How Enable LXP works</p>
            <h2 className="landing-section-title mb-3">From enrollment to measurable impact</h2>
            <p className="landing-section-desc mx-auto mb-0">
              A clear learning lifecycle your L&amp;D team can run every day.
            </p>
          </div>
          <div className="landing-steps reveal reveal-up">
            {HOW_IT_WORKS.map(({ step, title, desc }) => (
              <article key={step} className="landing-step">
                <div className="landing-step-num">{step}</div>
                <h3 className="fw-bold fs-5 mb-2">{title}</h3>
                <p className="text-muted small mb-0">{desc}</p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      {/* Enterprise band */}
      <section className="landing-enterprise-band py-5">
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={5}>
              <div className="landing-section-header-left reveal reveal-up">
                <p className="landing-section-eyebrow mb-2">Enterprise-ready</p>
                <h2 className="landing-section-title mb-3">
                  Scale learning across organizations with confidence
                </h2>
                <p className="landing-section-desc mb-4">
                  Enable LXP is built for Liberate’s enterprise customers — multi-org structure, governance, commerce, and deep reporting included.
                </p>
                <div className="d-flex flex-wrap gap-3 mb-4">
                  {PLATFORM_STATS.map(({ value, label }) => (
                    <div key={label} className="pe-3">
                      <div className="landing-stat-value" style={{ fontSize: '1.75rem' }}>
                        {value}
                      </div>
                      <p className="landing-stat-label mb-0">{label}</p>
                    </div>
                  ))}
                </div>
                <button type="button" className="landing-btn-primary" onClick={() => navigate('/enquiry')}>
                  Talk to Liberate
                  <FaArrowRight size={14} aria-hidden="true" />
                </button>
              </div>
            </Col>
            <Col lg={7}>
              <div className="reveal reveal-up">
                {ENTERPRISE_POINTS.map(({ title, desc }, index) => {
                  const Icon = [FaUsers, FaBuilding, FaBook, FaShieldAlt][index];
                  return (
                    <div key={title} className="landing-enterprise-item">
                      <span className="landing-enterprise-icon" aria-hidden="true">
                        <Icon />
                      </span>
                      <div>
                        <h3 className="fw-bold fs-6 mb-1">{title}</h3>
                        <p className="text-muted small mb-0">{desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Featured Courses */}
      <section id="courses" className="py-5 bg-white">
        <Container>
          <div className="text-center mb-5 reveal reveal-up">
            <p className="landing-section-eyebrow">Featured Courses</p>
            <h2 className="landing-section-title mb-3">Learning that fuels performance</h2>
            <p className="landing-section-desc mx-auto mb-0">
              Explore catalog courses designed for real workplace skills — then scale delivery inside Enable LXP.
            </p>
          </div>
          <FeaturedCourses />
          <div className="text-center mt-5 reveal">
            <button type="button" className="landing-btn-primary" onClick={() => navigate('/courses')}>
              View All Courses
              <FaArrowRight size={14} aria-hidden="true" />
            </button>
          </div>
        </Container>
      </section>

      {/* Awards — Liberate Global */}
      <section className="py-5 landing-about-band">
        <Container>
          <div className="text-center mb-5 reveal reveal-up">
            <p className="landing-section-eyebrow">Raising the bar with our clients</p>
            <h2 className="landing-section-title mb-3">Awards &amp; recognition</h2>
            <p className="landing-section-desc mx-auto mb-0">
              Where innovation in learning translates into industry recognition — from{' '}
              <a href={LIBERATE_CONTACT.website} target="_blank" rel="noopener noreferrer" className="landing-link-arrow">
                Liberate Global
              </a>
              .
            </p>
          </div>
          <Row className="g-4 justify-content-center">
            {LIBERATE_AWARDS.map((award) => (
              <Col md={6} lg={4} key={award.title}>
                <div className="landing-feature-card text-center reveal reveal-up h-100">
                  <div className="landing-stat-value mb-2">{award.value}</div>
                  <h4 className="fw-bold mb-2 fs-5">{award.title}</h4>
                  <p className="text-muted small mb-0">{award.description}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Testimonials — Liberate Global */}
      <section className="py-5 bg-white">
        <Container>
          <div className="text-center mb-5 reveal reveal-up">
            <p className="landing-section-eyebrow">Customer stories</p>
            <h2 className="landing-section-title mb-3">What Our Customers Say About Liberate</h2>
            <p className="landing-section-desc mx-auto mb-0">
              Stories from organizations we’ve helped create meaningful learning experiences.
            </p>
          </div>
          <Row className="g-4">
            {LIBERATE_TESTIMONIALS.map((testimonial) => (
              <Col lg={4} key={testimonial.name}>
                <div className="landing-testimonial-card reveal reveal-up">
                  <div className="d-flex mb-3 pt-4">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="text-warning me-1">
                        ★
                      </span>
                    ))}
                  </div>
                  <p className="text-secondary mb-4 ps-1" style={{ lineHeight: 1.7 }}>
                    &ldquo;{testimonial.content}&rdquo;
                  </p>
                  <div className="d-flex align-items-center">
                    <div
                      className="rounded-circle me-3 d-flex align-items-center justify-content-center fw-bold text-white"
                      style={{ width: 48, height: 48, background: '#B32025', flexShrink: 0 }}
                      aria-hidden="true"
                    >
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <h6 className="mb-0 fw-bold">{testimonial.name}</h6>
                      <p className="text-muted mb-0 small">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {landingPageData?.extra_content_html && (
        <section className="py-5 bg-white">
          <Container>
            <div className="extra-content" dangerouslySetInnerHTML={{ __html: landingPageData.extra_content_html }} />
          </Container>
        </section>
      )}

      {/* CTA */}
      {(!landingPageData || isSectionEnabled(landingPageData.show_banners)) && (
        <section className="landing-cta-band py-5 text-white">
          <Container>
            <Row className="justify-content-center text-center py-3">
              <Col lg={8}>
                <h2 className="landing-section-title text-white mb-3">
                  Ready to unlock continuous, personalized learning?
                </h2>
                <p className="lead mb-4 opacity-75">
                  Join organizations using Enable LXP — Liberate Global’s platform for courses, live training, skills, and EduGenie.
                </p>
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                  <button type="button" className="landing-btn-primary" onClick={() => navigate('/enquiry')}>
                    Book a Demo
                    <FaArrowRight size={14} aria-hidden="true" />
                  </button>
                  <button type="button" className="landing-btn-secondary" onClick={() => navigate('/login')}>
                    Sign In
                  </button>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
      )}

      {/* Footer */}
      <footer className="landing-footer py-5 mt-auto">
        <Container>
          <Row className="mb-4 g-4">
            <Col lg={4}>
              <LandingLogo variant="dark" layout="lockup" size="md" to={null} />
              <p className="mt-3 mb-3 small">
                Enable LXP is Liberate Global’s learning experience platform — courses, SCORM, VILT, assessments,
                certificates, gamification, analytics, and EduGenie in one enterprise stack.
              </p>
            </Col>
            <Col lg={2} md={6}>
              <h6 className="landing-footer-brand fw-bold mb-3">Quick Links</h6>
              <ul className="list-unstyled small">
                {[
                  { href: '/', label: 'Home' },
                  { href: '/#features', label: 'Features' },
                  { href: '/courses', label: 'Courses' },
                  { href: '/enquiry', label: 'Enquiry' },
                  { href: '/privacy-policy', label: 'Privacy Policy' },
                  { href: '/terms-of-use', label: 'Terms of Use' },
                ].map(({ href, label }) => (
                  <li key={href} className="mb-2">
                    <a href={href} className="landing-footer-link">
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </Col>
            <Col lg={3} md={6}>
              <h6 className="landing-footer-brand fw-bold mb-3">Platform</h6>
              <ul className="list-unstyled small">
                {[
                  'Courses & SCORM',
                  'Assessments & VILT',
                  'Certificates & Badges',
                  'EduGenie AI Assistant',
                  'Analytics & Reports',
                ].map((item) => (
                  <li key={item} className="mb-2">
                    <span className="landing-footer-link">{item}</span>
                  </li>
                ))}
              </ul>
            </Col>
            <Col lg={3}>
              <h6 className="landing-footer-brand fw-bold mb-3">Contact Liberate</h6>
              <p className="small mb-2">
                <a className="landing-footer-link" href={`mailto:${LIBERATE_CONTACT.salesEmail}`}>
                  {LIBERATE_CONTACT.salesEmail}
                </a>
              </p>
              <p className="small mb-1">AUS: {LIBERATE_CONTACT.phoneAustralia}</p>
              <p className="small mb-1">EU: {LIBERATE_CONTACT.phoneEurope}</p>
              <p className="small mb-2 opacity-75">{LIBERATE_CONTACT.hours}</p>
              <p className="small mb-2">
                {LIBERATE_CONTACT.address}
                <br />
                {LIBERATE_CONTACT.city}
              </p>
              <a
                href={LIBERATE_CONTACT.contactPage}
                target="_blank"
                rel="noopener noreferrer"
                className="landing-footer-link small"
              >
                liberateglobal.com/contact
              </a>
            </Col>
          </Row>
          <hr style={{ borderColor: 'rgba(255,255,255,0.1)' }} />
          <p className="text-center small mb-0 mt-4">
            © {new Date().getFullYear()} Enable LXP · A Liberate Global Platform. All rights reserved.
          </p>
        </Container>
      </footer>
    </div>
  );
};

export default LandingPage;
